import { parseISO, getUnixTime, subDays, format } from 'date-fns';

/**
 * Determines the last non-activity message between store and API messages.
 * @param {Object} messageInStore - The last non-activity message from the store.
 * @param {Object} messageFromAPI - The last non-activity message from the API.
 * @returns {Object} The latest non-activity message.
 */

const getLastNonActivityMessage = (messageInStore, messageFromAPI) => {
  // If both API value and store value for last non activity message
  // are available, then return the latest one.
  if (messageInStore && messageFromAPI) {
    return messageInStore.created_at >= messageFromAPI.created_at
      ? messageInStore
      : messageFromAPI;
  }
  // Otherwise, return whichever is available
  return messageInStore || messageFromAPI;
};

/**
 * Filters out duplicate source messages from an array of messages.
 * @param {Array} messages - The array of messages to filter.
 * @returns {Array} An array of messages without duplicates.
 */
export const filterDuplicateSourceMessages = (messages = []) => {
  const messagesWithoutDuplicates = [];
  // We cannot use Map or any short hand method as it returns the last message with the duplicate ID
  // We should return the message with smaller id when there is a duplicate
  messages.forEach(m1 => {
    if (m1.source_id) {
      const index = messagesWithoutDuplicates.findIndex(
        m2 => m1.source_id === m2.source_id
      );

      if (index < 0) {
        messagesWithoutDuplicates.push(m1);
      }
    } else {
      messagesWithoutDuplicates.push(m1);
    }
  });
  return messagesWithoutDuplicates;
};

/**
 * Retrieves the last message from a conversation, prioritizing non-activity messages.
 * @param {Object} m - The conversation object containing messages.
 * @returns {Object} The last message of the conversation.
 */
export const getLastMessage = m => {
  const lastMessageIncludingActivity = m.messages[m.messages.length - 1];

  const nonActivityMessages = m.messages.filter(
    message => message.message_type !== 2
  );
  const lastNonActivityMessageInStore =
    nonActivityMessages[nonActivityMessages.length - 1];

  const lastNonActivityMessageFromAPI = m.last_non_activity_message;

  // If API value and store value for last non activity message
  // is empty, then return the last activity message
  if (!lastNonActivityMessageInStore && !lastNonActivityMessageFromAPI) {
    return lastMessageIncludingActivity;
  }

  return getLastNonActivityMessage(
    lastNonActivityMessageInStore,
    lastNonActivityMessageFromAPI
  );
};

/**
 * Filters messages that have been read by the agent.
 * @param {Array} messages - The array of messages to filter.
 * @param {number} agentLastSeenAt - The timestamp of when the agent last saw the messages.
 * @returns {Array} An array of read messages.
 */
export const getReadMessages = (messages, agentLastSeenAt) => {
  return messages.filter(
    message => message.created_at * 1000 <= agentLastSeenAt * 1000
  );
};

/**
 * Filters messages that have not been read by the agent.
 * @param {Array} messages - The array of messages to filter.
 * @param {number} agentLastSeenAt - The timestamp of when the agent last saw the messages.
 * @returns {Array} An array of unread messages.
 */
export const getUnreadMessages = (messages, agentLastSeenAt) => {
  return messages.filter(
    message => message.created_at * 1000 > agentLastSeenAt * 1000
  );
};

const getRecursive = (obj, path, defaultValue) => {
  const keys = Array.isArray(path) ? path : path.split('.');
  return keys.reduce((acc, key) => {
    return acc && acc[key] !== undefined ? acc[key] : defaultValue;
  }, obj);
};

const filterKeyMap = {
  status: 'status',
  inbox_id: 'inbox_id',
  campaign_id: 'campaign_id',
  labels: 'labels',
  display_id: 'id',
  assignee_id: 'meta.assignee.id',
  team_id: 'meta.team.id',
  created_at: 'created_at',
  last_activity_at: 'last_activity_at',
};

// Filters that cannot be evaluated client-side because their data is not
// present on the serialized conversation object — they are resolved entirely by
// the backend (`/conversations/filter`) and must be treated as pass-through
// here, otherwise the custom-filter view would re-filter and drop every row the
// backend already matched.
//
// Includes the Claudia-mirror fields: satellite tables
// (conversation_resolutions / conversation_feedbacks), per-message columns, and
// the conversations columns (helpdesk_id/ab_key/uuid) resolved server-side.
// `uuid` MUST be here: the backend compares `conversations.uuid::text`, but the
// client would look it up under `custom_attributes.uuid` (undefined) and drop
// every row the backend already matched.
const BACKEND_ONLY_FILTERS = new Set([
  'instagram_interaction_type',
  'slack_channel_id',
  'project_name',
  'helpdesk_id',
  'ab_key',
  'resolution_type',
  'resolution_reason',
  'gen_cx_score',
  'resolved_at',
  'audited',
  'topic',
  'flow_session_id',
  'used_sections',
  'uuid',
  'has_failed_outgoing_message',
]);

const RECHECKABLE_OPERATORS = new Set(['equal_to']);

const RECHECKABLE_ATTRIBUTES = new Set([
  'status',
  'inbox_id',
  'assignee_id',
  'team_id',
  'labels',
  'display_id',
]);

export const isClientRecheckableFilter = filter =>
  RECHECKABLE_OPERATORS.has(filter?.filter_operator) &&
  filter?.attribute_model !== 'custom_attributes' &&
  !filter?.custom_attribute_type &&
  RECHECKABLE_ATTRIBUTES.has(filter?.attribute_key);

// Helper: detect YYYY-MM-DD date-only
const isDateOnlyValue = value => /^\d{4}-\d{2}-\d{2}$/.test(String(value));

// Helper: normalize any supported value (epoch seconds, ISO string) to YYYY-MM-DD or ''
const toDateOnlyString = val => {
  if (val === undefined || val === null) return '';
  try {
    if (typeof val === 'number') {
      return format(new Date(val * 1000), 'yyyy-MM-dd');
    }
    const d = parseISO(String(val));
    if (isNaN(d)) return '';
    return format(d, 'yyyy-MM-dd');
  } catch (e) {
    return '';
  }
};

// Helper: convert value to epoch seconds (number) or null if invalid
const toEpoch = val => {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  try {
    const d = parseISO(String(val));
    if (isNaN(d)) return null;
    return getUnixTime(d);
  } catch (e) {
    return null;
  }
};

// Helper: map filter values to valid epoch seconds
const mapFilterEpochs = values => values
  .map(v => toEpoch(v))
  .filter(v => v !== null);

// Helper: day-level comparisons (convDays: normalized conversation values)
const anyDayEqual = (convValues, filterValues) => filterValues.some(fv => {
  if (!isDateOnlyValue(fv)) return false;
  const target = String(fv);
  return convValues.some(cv => toDateOnlyString(cv) === target);
});

const allDayNotEqual = (convValues, filterValues) => convValues.every(cv => {
  const day = toDateOnlyString(cv);
  return filterValues.every(fv => !isDateOnlyValue(fv) || day !== String(fv));
});

const anyDayLessThan = (convValues, filterValues) => filterValues.some(fv => {
  if (!isDateOnlyValue(fv)) return false;
  const target = String(fv);
  return convValues.some(cv => {
    const day = toDateOnlyString(cv);
    return day !== '' && day <= target;
  });
});

const anyDayGreaterThan = (convValues, filterValues) => filterValues.some(fv => {
  if (!isDateOnlyValue(fv)) return false;
  const target = String(fv);
  return convValues.some(cv => {
    const day = toDateOnlyString(cv);
    return day !== '' && day >= target;
  });
});

export const applyActiveFiltersToConversations = (
  conversations,
  queryPayload
) => {
  if (!queryPayload || queryPayload.length === 0) return conversations;

  const evaluateFilter = (conversation, filter) => {
    const {
      attribute_key: filterKey,
      filter_operator: operator,
      values,
    } = filter;

    // Backend-only filters are resolved server-side; skip client-side evaluation
    // (their data is not on the conversation object — see BACKEND_ONLY_FILTERS).
    if (BACKEND_ONLY_FILTERS.has(filterKey)) return true;

    let conversationValue;
    if (Object.prototype.hasOwnProperty.call(filterKeyMap, filterKey)) {
      conversationValue = getRecursive(conversation, filterKeyMap[filterKey]);
    } else {
      conversationValue = conversation.custom_attributes
        ? conversation.custom_attributes[filterKey]
        : undefined;
    }

    const conversationValues = Array.isArray(conversationValue)
      ? conversationValue.flat()
      : [conversationValue];

    const considerAll = filterKey === 'status';

    switch (operator) {
      case 'equal_to':
        if (values.some(isDateOnlyValue)) {
          return anyDayEqual(conversationValues, values) || (values.some(v => v === 'all') && considerAll);
        }
        return values.some(value => {
          if (value === 'all' && considerAll) return true;
          return conversationValues.some(
            convVal => String(convVal).toLowerCase() === String(value).toLowerCase()
          );
        });
      case 'not_equal_to':
        if (values.some(isDateOnlyValue)) {
          return allDayNotEqual(conversationValues, values) && !values.includes('all');
        }
        return conversationValues.every(convVal =>
          values.every(value => {
            if (value === 'all' && considerAll) return false;
            return String(convVal).toLowerCase() !== String(value).toLowerCase();
          })
        );
      case 'contains':
        return values.some(value =>
          conversationValues.some(convVal =>
            String(convVal).toLowerCase().includes(String(value).toLowerCase())
          )
        );
      case 'is_present':
        return conversationValues.some(value => Boolean(value));
      case 'is_not_present':
        return conversationValues.every(value => !value);
      case 'is_less_than': {
        if (!conversationValues.length || values.length === 0) return false;
        if (values.some(isDateOnlyValue)) {
          return anyDayLessThan(conversationValues, values);
        }
        const filterEpochs = mapFilterEpochs(values);
        if (!filterEpochs.length) return false;
        return conversationValues.some(cv => {
          const ep = toEpoch(cv);
          return ep !== null && filterEpochs.some(f => ep <= f);
        });
      }
      case 'is_greater_than': {
        if (!conversationValues.length || values.length === 0) return false;
        if (values.some(isDateOnlyValue)) {
          return anyDayGreaterThan(conversationValues, values);
        }
        const filterEpochs = mapFilterEpochs(values);
        if (!filterEpochs.length) return false;
        return conversationValues.some(cv => {
          const ep = toEpoch(cv);
          return ep !== null && filterEpochs.some(f => ep >= f);
        });
      }
      case 'days_before': {
        if (!conversationValues.length || values.length === 0) return false;

        const daysBefore = parseInt(values[0], 10); // Directly extract the first and only value
        if (Number.isNaN(daysBefore)) return false; // Ensure it's a valid number

        const thresholdDate = getUnixTime(subDays(new Date(), daysBefore)); // Calculate the threshold date in epoch seconds

        return conversationValues.some(
          convVal => typeof convVal === 'number' && convVal < thresholdDate // Check if conversation timestamp is older than the threshold
        );
      }
      default:
        return true;
    }
  };

  const groupFilters = filters => {
    const groups = [];
    let currentGroup = [];
    let currentOperator = 'and';

    filters.forEach((filter, index) => {
      const operator = (filter.query_operator || 'and').toLowerCase();

      if (operator === 'and') {
        currentGroup.push(filter);
      } else if (operator === 'or') {
        // Push the current group before starting a new OR block
        if (currentGroup.length > 0) {
          groups.push({ operator: currentOperator, filters: currentGroup });
        }
        currentGroup = [filter];
        currentOperator = 'or';
      }

      // Push the last group at the end of the loop
      if (index === filters.length - 1 && currentGroup.length > 0) {
        groups.push({ operator: currentOperator, filters: currentGroup });
      }
    });

    return groups;
  };

  const evaluateGroup = (conversation, group) => {
    const { operator, filters } = group;

    if (operator === 'and') {
      return filters.every(filter => evaluateFilter(conversation, filter));
    }
    if (operator === 'or') {
      return filters.some(filter => evaluateFilter(conversation, filter));
    }
    return true;
  };

  const filterGroups = groupFilters(queryPayload);

  return conversations.filter(conversation => {
    return filterGroups.some(group => evaluateGroup(conversation, group));
  });
};
